import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { NavBar } from '../components/NavBar';
import { Card } from '../components/SubscriptionAppCard';
import { Badge } from '../components/SubscriptionAppBadge';
import { StatCard, TrendIndicator } from '../components/StatCard';
import { ChevronDown, ChevronUp, Info, AlertTriangle, ExternalLink } from 'lucide-react';
export function PaymentRecoveryPage() {
  const navigate = useNavigate();
  // State
  const [smartRecoveryEnabled, setSmartRecoveryEnabled] = useState(true);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  // Retry attempts state
  const [retry1Enabled, setRetry1Enabled] = useState(true);
  const [retry1Days, setRetry1Days] = useState(3);
  const [retry1Time, setRetry1Time] = useState('9:00 AM');
  const [retry1Email, setRetry1Email] = useState(true);
  const [retry1SMS, setRetry1SMS] = useState(true);
  const [retry1Expanded, setRetry1Expanded] = useState(false);
  const [retry2Enabled, setRetry2Enabled] = useState(true);
  const [retry2Days, setRetry2Days] = useState(5);
  const [retry2Time, setRetry2Time] = useState('9:00 AM');
  const [retry2Email, setRetry2Email] = useState(true);
  const [retry2SMS, setRetry2SMS] = useState(true);
  const [retry2Expanded, setRetry2Expanded] = useState(false);
  const [retry3Enabled, setRetry3Enabled] = useState(true);
  const [retry3Days, setRetry3Days] = useState(7);
  const [retry3Time, setRetry3Time] = useState('9:00 AM');
  const [retry3Email, setRetry3Email] = useState(true);
  const [retry3SMS, setRetry3SMS] = useState(false);
  const [retry3Expanded, setRetry3Expanded] = useState(false);
  const [retry4Enabled, setRetry4Enabled] = useState(false);
  const [retry4Days, setRetry4Days] = useState(10);
  const [retry4Time, setRetry4Time] = useState('9:00 AM');
  const [retry4Email, setRetry4Email] = useState(true);
  const [retry4SMS, setRetry4SMS] = useState(false);
  const [retry4Expanded, setRetry4Expanded] = useState(false);
  // Email templates state
  const [initialFailureEmail, setInitialFailureEmail] = useState(true);
  const [retryReminderEmail, setRetryReminderEmail] = useState(true);
  const [paymentRecoveredEmail, setPaymentRecoveredEmail] = useState(true);
  const [finalWarningEmail, setFinalWarningEmail] = useState(true);
  const [subscriptionOnHoldEmail, setSubscriptionOnHoldEmail] = useState(true);
  // SMS state
  const [smsEnabled, setSmsEnabled] = useState(false);
  // Payment link state
  const [paymentLinkEnabled, setPaymentLinkEnabled] = useState(true);
  const [linkExpiration, setLinkExpiration] = useState('7 days');
  // Internal notifications state
  const [notifyOnFailure, setNotifyOnFailure] = useState(true);
  const [notifyOnRecovery, setNotifyOnRecovery] = useState(false);
  const [notifyOnFallback, setNotifyOnFallback] = useState(true);
  const [dailySummary, setDailySummary] = useState(false);
  const [notificationEmails, setNotificationEmails] = useState(['orders@acmewine.com']);
  const [newEmail, setNewEmail] = useState('');
  const saveSetting = (message: string = 'Settings saved') => {
    setToastMessage(message);
    setShowToast(true);
    setTimeout(() => setShowToast(false), 3000);
  };
  const handleStatCardClick = (type: string) => {
    console.log(`Navigate to ${type}`);
  };
  const handleAddEmail = () => {
    if (newEmail && newEmail.includes('@')) {
      setNotificationEmails([...notificationEmails, newEmail]);
      setNewEmail('');
      saveSetting('Email added');
    }
  };
  const handleRemoveEmail = (emailToRemove: string) => {
    setNotificationEmails(notificationEmails.filter(email => email !== emailToRemove));
    saveSetting('Email removed');
  };
  const daysOptions = [1, 2, 3, 5, 7, 10, 14];
  const timeOptions = ['6:00 AM', '7:00 AM', '8:00 AM', '9:00 AM', '10:00 AM', '11:00 AM', '12:00 PM', '1:00 PM', '2:00 PM', '3:00 PM', '4:00 PM', '5:00 PM', '6:00 PM', '7:00 PM', '8:00 PM', '9:00 PM'];
  return <div className="bg-[#F1F5F9] min-h-full p-6" data-id="element-8024">
      {/* NavBar */}
      <div className="mb-6" data-id="element-8025">
        <NavBar tabs={[]} title="Payment Recovery" subtitle="Automate failed payment retries and reduce involuntary churn with smart dunning" data-id="element-8026" />
      </div>

      {/* Section 1: Recovery Overview Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8" data-id="element-8027">
        <StatCard label="Failed Payments" value={4} secondaryValue="$1,240 at risk" badge={{
        text: 'Action Needed',
        variant: 'error'
      }} onClick={() => handleStatCardClick('failed')} data-id="element-8028" />

        <StatCard label="In Recovery" value={3} secondaryValue="Active retry sequences" badge={{
        text: 'Retrying',
        variant: 'warning'
      }} data-id="element-8029" />

        <StatCard label="Recovered This Month" value={12} secondaryValue="$2,847 recovered" trend={<TrendIndicator value={18} label="vs last month" direction="up" data-id="element-8031" />} data-id="element-8030" />

        <StatCard label="Recovery Rate" value="73%" secondaryValue="Industry avg: 65%" trend={<TrendIndicator value={5} label="vs last month" direction="up" data-id="element-8033" />} data-id="element-8032" />
      </div>

      <hr className="my-10 border-[#CBD5E1]" data-id="element-8034" />

      {/* Main Content */}
      <div className="space-y-6" data-id="element-8035">
        {/* Section 2: Smart Recovery Toggle */}
        <Card data-id="element-8036">
          <div className="mb-6" data-id="element-8037">
            <h2 className="text-[20px] font-semibold text-[#0F172A]" data-id="element-8038">
              Smart Recovery
            </h2>
            <p className="text-[14px] text-[#64748B] mt-1" data-id="element-8039">
              Automatically retry failed payments using optimized timing
            </p>
          </div>

          <div className="flex items-start justify-between py-4 border-b border-[#E2E8F0]" data-id="element-8040">
            <div className="flex-1 pr-6" data-id="element-8041">
              <h3 className="text-[16px] font-semibold text-[#0F172A] mb-1" data-id="element-8042">
                Enable Smart Recovery
              </h3>
              <p className="text-[14px] text-[#64748B]" data-id="element-8043">
                When enabled, WinePik will automatically retry failed payments
                based on your configured schedule. Customers will be notified at
                each step.
              </p>
            </div>
            <button onClick={() => {
            setSmartRecoveryEnabled(!smartRecoveryEnabled);
            saveSetting();
          }} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:ring-offset-2 flex-shrink-0 ${smartRecoveryEnabled ? 'bg-[#0EA5E9]' : 'bg-[#CBD5E1]'}`} role="switch" aria-checked={smartRecoveryEnabled} data-id="element-8044">
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${smartRecoveryEnabled ? 'translate-x-6' : 'translate-x-1'}`} data-id="element-8045" />
            </button>
          </div>

          {smartRecoveryEnabled && <div className="mt-4 p-4 bg-[#E0F2FE] border border-[#7DD3FC] rounded-md flex items-start gap-3" data-id="element-8046">
              <Info className="w-5 h-5 text-[#0369A1] flex-shrink-0 mt-0.5" data-id="element-8047" />
              <p className="text-[14px] text-[#0369A1]" data-id="element-8048">
                Smart Recovery analyzes payment failure reasons and optimizes
                retry timing for best results. Most failed payments recover
                within 2-3 retry attempts.
              </p>
            </div>}
        </Card>

        {/* Section 3: Retry Schedule */}
        <Card data-id="element-8049">
          <div className="mb-6" data-id="element-8050">
            <h2 className="text-[20px] font-semibold text-[#0F172A]" data-id="element-8051">
              Retry Schedule
            </h2>
            <p className="text-[14px] text-[#64748B] mt-1" data-id="element-8052">
              Configure when and how often to retry failed payments
            </p>
          </div>

          <div className={`space-y-3 ${!smartRecoveryEnabled ? 'opacity-50 pointer-events-none' : ''}`} data-id="element-8053">
            {/* Retry 1 */}
            <div className="border border-[#E2E8F0] rounded-lg overflow-hidden" data-id="element-8054">
              <div className="flex items-center justify-between p-4 bg-[#F8FAFC] cursor-pointer hover:bg-[#F1F5F9] transition-colors" onClick={() => setRetry1Expanded(!retry1Expanded)} data-id="element-8055">
                <div className="flex items-center gap-3" data-id="element-8056">
                  <span className="text-[14px] font-semibold text-[#0F172A]" data-id="element-8057">
                    Retry 1
                  </span>
                </div>
                <div className="flex items-center gap-3" data-id="element-8058">
                  <button onClick={e => {
                  e.stopPropagation();
                  setRetry1Enabled(!retry1Enabled);
                  saveSetting();
                }} className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${retry1Enabled ? 'bg-[#0EA5E9]' : 'bg-[#CBD5E1]'}`} data-id="element-8059">
                    <span className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${retry1Enabled ? 'translate-x-5' : 'translate-x-1'}`} data-id="element-8060" />
                  </button>
                  {retry1Expanded ? <ChevronUp className="w-5 h-5 text-[#64748B]" data-id="element-8061" /> : <ChevronDown className="w-5 h-5 text-[#64748B]" data-id="element-8062" />}
                </div>
              </div>

              {retry1Expanded && <div className="p-4 space-y-4 bg-white border-t border-[#E2E8F0]" data-id="element-8063">
                  <div className="grid grid-cols-2 gap-4" data-id="element-8064">
                    <div data-id="element-8065">
                      <label className="block text-[12px] font-medium text-[#475569] mb-2" data-id="element-8066">
                        Wait
                      </label>
                      <div className="flex items-center gap-2" data-id="element-8067">
                        <select value={retry1Days} onChange={e => {
                      setRetry1Days(parseInt(e.target.value));
                      saveSetting();
                    }} className="flex-1 h-[40px] pl-3 pr-10 border border-[#CBD5E1] rounded-md text-[14px] text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:border-transparent appearance-none bg-no-repeat bg-right bg-white" style={{
                      backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%23475569' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
                      backgroundPosition: 'right 12px center',
                      backgroundSize: '16px 16px'
                    }} data-id="element-8068">
                          {daysOptions.map(day => <option key={day} value={day} data-id="element-8069">
                              {day}
                            </option>)}
                        </select>
                        <span className="text-[14px] text-[#64748B]" data-id="element-8070">
                          days after initial failure
                        </span>
                      </div>
                    </div>

                    <div data-id="element-8071">
                      <label className="block text-[12px] font-medium text-[#475569] mb-2" data-id="element-8072">
                        Time
                      </label>
                      <select value={retry1Time} onChange={e => {
                    setRetry1Time(e.target.value);
                    saveSetting();
                  }} className="w-full h-[40px] pl-3 pr-10 border border-[#CBD5E1] rounded-md text-[14px] text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:border-transparent appearance-none bg-no-repeat bg-right bg-white" style={{
                    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%23475569' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
                    backgroundPosition: 'right 12px center',
                    backgroundSize: '16px 16px'
                  }} data-id="element-8073">
                        {timeOptions.map(time => <option key={time} value={time} data-id="element-8074">
                            {time}
                          </option>)}
                      </select>
                      <p className="text-[12px] text-[#64748B] mt-1" data-id="element-8075">
                        In customer's timezone
                      </p>
                    </div>
                  </div>

                  <div data-id="element-8076">
                    <label className="block text-[12px] font-medium text-[#475569] mb-2" data-id="element-8077">
                      Notify
                    </label>
                    <div className="flex items-center gap-4" data-id="element-8078">
                      <label className="flex items-center gap-2 cursor-pointer" data-id="element-8079">
                        <input type="checkbox" checked={retry1Email} onChange={e => {
                      setRetry1Email(e.target.checked);
                      saveSetting();
                    }} className="w-4 h-4 text-[#0EA5E9] border-[#CBD5E1] rounded focus:ring-[#0EA5E9]" data-id="element-8080" />
                        <span className="text-[14px] text-[#0F172A]" data-id="element-8081">
                          Send email
                        </span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer" data-id="element-8082">
                        <input type="checkbox" checked={retry1SMS} onChange={e => {
                      setRetry1SMS(e.target.checked);
                      saveSetting();
                    }} className="w-4 h-4 text-[#0EA5E9] border-[#CBD5E1] rounded focus:ring-[#0EA5E9]" data-id="element-8083" />
                        <span className="text-[14px] text-[#0F172A]" data-id="element-8084">
                          Send SMS
                        </span>
                      </label>
                    </div>
                  </div>
                </div>}
            </div>

            {/* Retry 2 */}
            <div className="border border-[#E2E8F0] rounded-lg overflow-hidden" data-id="element-8085">
              <div className="flex items-center justify-between p-4 bg-[#F8FAFC] cursor-pointer hover:bg-[#F1F5F9] transition-colors" onClick={() => setRetry2Expanded(!retry2Expanded)} data-id="element-8086">
                <div className="flex items-center gap-3" data-id="element-8087">
                  <span className="text-[14px] font-semibold text-[#0F172A]" data-id="element-8088">
                    Retry 2
                  </span>
                </div>
                <div className="flex items-center gap-3" data-id="element-8089">
                  <button onClick={e => {
                  e.stopPropagation();
                  setRetry2Enabled(!retry2Enabled);
                  saveSetting();
                }} className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${retry2Enabled ? 'bg-[#0EA5E9]' : 'bg-[#CBD5E1]'}`} data-id="element-8090">
                    <span className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${retry2Enabled ? 'translate-x-5' : 'translate-x-1'}`} data-id="element-8091" />
                  </button>
                  {retry2Expanded ? <ChevronUp className="w-5 h-5 text-[#64748B]" data-id="element-8092" /> : <ChevronDown className="w-5 h-5 text-[#64748B]" data-id="element-8093" />}
                </div>
              </div>

              {retry2Expanded && <div className="p-4 space-y-4 bg-white border-t border-[#E2E8F0]" data-id="element-8094">
                  <div className="grid grid-cols-2 gap-4" data-id="element-8095">
                    <div data-id="element-8096">
                      <label className="block text-[12px] font-medium text-[#475569] mb-2" data-id="element-8097">
                        Wait
                      </label>
                      <div className="flex items-center gap-2" data-id="element-8098">
                        <select value={retry2Days} onChange={e => {
                      setRetry2Days(parseInt(e.target.value));
                      saveSetting();
                    }} className="flex-1 h-[40px] pl-3 pr-10 border border-[#CBD5E1] rounded-md text-[14px] text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:border-transparent appearance-none bg-no-repeat bg-right bg-white" style={{
                      backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%23475569' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
                      backgroundPosition: 'right 12px center',
                      backgroundSize: '16px 16px'
                    }} data-id="element-8099">
                          {daysOptions.map(day => <option key={day} value={day} data-id="element-8100">
                              {day}
                            </option>)}
                        </select>
                        <span className="text-[14px] text-[#64748B]" data-id="element-8101">
                          days after Retry 1
                        </span>
                      </div>
                    </div>

                    <div data-id="element-8102">
                      <label className="block text-[12px] font-medium text-[#475569] mb-2" data-id="element-8103">
                        Time
                      </label>
                      <select value={retry2Time} onChange={e => {
                    setRetry2Time(e.target.value);
                    saveSetting();
                  }} className="w-full h-[40px] pl-3 pr-10 border border-[#CBD5E1] rounded-md text-[14px] text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:border-transparent appearance-none bg-no-repeat bg-right bg-white" style={{
                    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%23475569' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
                    backgroundPosition: 'right 12px center',
                    backgroundSize: '16px 16px'
                  }} data-id="element-8104">
                        {timeOptions.map(time => <option key={time} value={time} data-id="element-8105">
                            {time}
                          </option>)}
                      </select>
                      <p className="text-[12px] text-[#64748B] mt-1" data-id="element-8106">
                        In customer's timezone
                      </p>
                    </div>
                  </div>

                  <div data-id="element-8107">
                    <label className="block text-[12px] font-medium text-[#475569] mb-2" data-id="element-8108">
                      Notify
                    </label>
                    <div className="flex items-center gap-4" data-id="element-8109">
                      <label className="flex items-center gap-2 cursor-pointer" data-id="element-8110">
                        <input type="checkbox" checked={retry2Email} onChange={e => {
                      setRetry2Email(e.target.checked);
                      saveSetting();
                    }} className="w-4 h-4 text-[#0EA5E9] border-[#CBD5E1] rounded focus:ring-[#0EA5E9]" data-id="element-8111" />
                        <span className="text-[14px] text-[#0F172A]" data-id="element-8112">
                          Send email
                        </span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer" data-id="element-8113">
                        <input type="checkbox" checked={retry2SMS} onChange={e => {
                      setRetry2SMS(e.target.checked);
                      saveSetting();
                    }} className="w-4 h-4 text-[#0EA5E9] border-[#CBD5E1] rounded focus:ring-[#0EA5E9]" data-id="element-8114" />
                        <span className="text-[14px] text-[#0F172A]" data-id="element-8115">
                          Send SMS
                        </span>
                      </label>
                    </div>
                  </div>
                </div>}
            </div>

            {/* Retry 3 */}
            <div className="border border-[#E2E8F0] rounded-lg overflow-hidden" data-id="element-8116">
              <div className="flex items-center justify-between p-4 bg-[#F8FAFC] cursor-pointer hover:bg-[#F1F5F9] transition-colors" onClick={() => setRetry3Expanded(!retry3Expanded)} data-id="element-8117">
                <div className="flex items-center gap-3" data-id="element-8118">
                  <span className="text-[14px] font-semibold text-[#0F172A]" data-id="element-8119">
                    Retry 3
                  </span>
                </div>
                <div className="flex items-center gap-3" data-id="element-8120">
                  <button onClick={e => {
                  e.stopPropagation();
                  setRetry3Enabled(!retry3Enabled);
                  saveSetting();
                }} className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${retry3Enabled ? 'bg-[#0EA5E9]' : 'bg-[#CBD5E1]'}`} data-id="element-8121">
                    <span className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${retry3Enabled ? 'translate-x-5' : 'translate-x-1'}`} data-id="element-8122" />
                  </button>
                  {retry3Expanded ? <ChevronUp className="w-5 h-5 text-[#64748B]" data-id="element-8123" /> : <ChevronDown className="w-5 h-5 text-[#64748B]" data-id="element-8124" />}
                </div>
              </div>

              {retry3Expanded && <div className="p-4 space-y-4 bg-white border-t border-[#E2E8F0]" data-id="element-8125">
                  <div className="grid grid-cols-2 gap-4" data-id="element-8126">
                    <div data-id="element-8127">
                      <label className="block text-[12px] font-medium text-[#475569] mb-2" data-id="element-8128">
                        Wait
                      </label>
                      <div className="flex items-center gap-2" data-id="element-8129">
                        <select value={retry3Days} onChange={e => {
                      setRetry3Days(parseInt(e.target.value));
                      saveSetting();
                    }} className="flex-1 h-[40px] pl-3 pr-10 border border-[#CBD5E1] rounded-md text-[14px] text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:border-transparent appearance-none bg-no-repeat bg-right bg-white" style={{
                      backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%23475569' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
                      backgroundPosition: 'right 12px center',
                      backgroundSize: '16px 16px'
                    }} data-id="element-8130">
                          {daysOptions.map(day => <option key={day} value={day} data-id="element-8131">
                              {day}
                            </option>)}
                        </select>
                        <span className="text-[14px] text-[#64748B]" data-id="element-8132">
                          days after Retry 2
                        </span>
                      </div>
                    </div>

                    <div data-id="element-8133">
                      <label className="block text-[12px] font-medium text-[#475569] mb-2" data-id="element-8134">
                        Time
                      </label>
                      <select value={retry3Time} onChange={e => {
                    setRetry3Time(e.target.value);
                    saveSetting();
                  }} className="w-full h-[40px] pl-3 pr-10 border border-[#CBD5E1] rounded-md text-[14px] text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:border-transparent appearance-none bg-no-repeat bg-right bg-white" style={{
                    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%23475569' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
                    backgroundPosition: 'right 12px center',
                    backgroundSize: '16px 16px'
                  }} data-id="element-8135">
                        {timeOptions.map(time => <option key={time} value={time} data-id="element-8136">
                            {time}
                          </option>)}
                      </select>
                      <p className="text-[12px] text-[#64748B] mt-1" data-id="element-8137">
                        In customer's timezone
                      </p>
                    </div>
                  </div>

                  <div data-id="element-8138">
                    <label className="block text-[12px] font-medium text-[#475569] mb-2" data-id="element-8139">
                      Notify
                    </label>
                    <div className="flex items-center gap-4" data-id="element-8140">
                      <label className="flex items-center gap-2 cursor-pointer" data-id="element-8141">
                        <input type="checkbox" checked={retry3Email} onChange={e => {
                      setRetry3Email(e.target.checked);
                      saveSetting();
                    }} className="w-4 h-4 text-[#0EA5E9] border-[#CBD5E1] rounded focus:ring-[#0EA5E9]" data-id="element-8142" />
                        <span className="text-[14px] text-[#0F172A]" data-id="element-8143">
                          Send email
                        </span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer" data-id="element-8144">
                        <input type="checkbox" checked={retry3SMS} onChange={e => {
                      setRetry3SMS(e.target.checked);
                      saveSetting();
                    }} className="w-4 h-4 text-[#0EA5E9] border-[#CBD5E1] rounded focus:ring-[#0EA5E9]" data-id="element-8145" />
                        <span className="text-[14px] text-[#0F172A]" data-id="element-8146">
                          Send SMS
                        </span>
                      </label>
                    </div>
                  </div>
                </div>}
            </div>

            {/* Retry 4 (Final) */}
            <div className="border border-[#E2E8F0] rounded-lg overflow-hidden" data-id="element-8147">
              <div className="flex items-center justify-between p-4 bg-[#F8FAFC] cursor-pointer hover:bg-[#F1F5F9] transition-colors" onClick={() => setRetry4Expanded(!retry4Expanded)} data-id="element-8148">
                <div className="flex items-center gap-3" data-id="element-8149">
                  <span className="text-[14px] font-semibold text-[#0F172A]" data-id="element-8150">
                    Retry 4 (Final)
                  </span>
                </div>
                <div className="flex items-center gap-3" data-id="element-8151">
                  <button onClick={e => {
                  e.stopPropagation();
                  setRetry4Enabled(!retry4Enabled);
                  saveSetting();
                }} className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${retry4Enabled ? 'bg-[#0EA5E9]' : 'bg-[#CBD5E1]'}`} data-id="element-8152">
                    <span className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${retry4Enabled ? 'translate-x-5' : 'translate-x-1'}`} data-id="element-8153" />
                  </button>
                  {retry4Expanded ? <ChevronUp className="w-5 h-5 text-[#64748B]" data-id="element-8154" /> : <ChevronDown className="w-5 h-5 text-[#64748B]" data-id="element-8155" />}
                </div>
              </div>

              {retry4Expanded && <div className="p-4 space-y-4 bg-white border-t border-[#E2E8F0]" data-id="element-8156">
                  <div className="grid grid-cols-2 gap-4" data-id="element-8157">
                    <div data-id="element-8158">
                      <label className="block text-[12px] font-medium text-[#475569] mb-2" data-id="element-8159">
                        Wait
                      </label>
                      <div className="flex items-center gap-2" data-id="element-8160">
                        <select value={retry4Days} onChange={e => {
                      setRetry4Days(parseInt(e.target.value));
                      saveSetting();
                    }} className="flex-1 h-[40px] pl-3 pr-10 border border-[#CBD5E1] rounded-md text-[14px] text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:border-transparent appearance-none bg-no-repeat bg-right bg-white" style={{
                      backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%23475569' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
                      backgroundPosition: 'right 12px center',
                      backgroundSize: '16px 16px'
                    }} data-id="element-8161">
                          {daysOptions.map(day => <option key={day} value={day} data-id="element-8162">
                              {day}
                            </option>)}
                        </select>
                        <span className="text-[14px] text-[#64748B]" data-id="element-8163">
                          days after Retry 3
                        </span>
                      </div>
                    </div>

                    <div data-id="element-8164">
                      <label className="block text-[12px] font-medium text-[#475569] mb-2" data-id="element-8165">
                        Time
                      </label>
                      <select value={retry4Time} onChange={e => {
                    setRetry4Time(e.target.value);
                    saveSetting();
                  }} className="w-full h-[40px] pl-3 pr-10 border border-[#CBD5E1] rounded-md text-[14px] text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:border-transparent appearance-none bg-no-repeat bg-right bg-white" style={{
                    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%23475569' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
                    backgroundPosition: 'right 12px center',
                    backgroundSize: '16px 16px'
                  }} data-id="element-8166">
                        {timeOptions.map(time => <option key={time} value={time} data-id="element-8167">
                            {time}
                          </option>)}
                      </select>
                      <p className="text-[12px] text-[#64748B] mt-1" data-id="element-8168">
                        In customer's timezone
                      </p>
                    </div>
                  </div>

                  <div data-id="element-8169">
                    <label className="block text-[12px] font-medium text-[#475569] mb-2" data-id="element-8170">
                      Notify
                    </label>
                    <div className="flex items-center gap-4" data-id="element-8171">
                      <label className="flex items-center gap-2 cursor-pointer" data-id="element-8172">
                        <input type="checkbox" checked={retry4Email} onChange={e => {
                      setRetry4Email(e.target.checked);
                      saveSetting();
                    }} className="w-4 h-4 text-[#0EA5E9] border-[#CBD5E1] rounded focus:ring-[#0EA5E9]" data-id="element-8173" />
                        <span className="text-[14px] text-[#0F172A]" data-id="element-8174">
                          Send email
                        </span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer" data-id="element-8175">
                        <input type="checkbox" checked={retry4SMS} onChange={e => {
                      setRetry4SMS(e.target.checked);
                      saveSetting();
                    }} className="w-4 h-4 text-[#0EA5E9] border-[#CBD5E1] rounded focus:ring-[#0EA5E9]" data-id="element-8176" />
                        <span className="text-[14px] text-[#0F172A]" data-id="element-8177">
                          Send SMS
                        </span>
                      </label>
                    </div>
                  </div>

                  <div className="p-3 bg-[#FEF3C7] border border-[#FDE68A] rounded-md flex items-start gap-2" data-id="element-8178">
                    <AlertTriangle className="w-4 h-4 text-[#F59E0B] flex-shrink-0 mt-0.5" data-id="element-8179" />
                    <p className="text-[13px] text-[#92400E]" data-id="element-8180">
                      This is the final attempt before fallback action
                    </p>
                  </div>
                </div>}
            </div>
          </div>
        </Card>

        {/* Section 4: Fallback Action */}
        <Card data-id="element-8181">
          <div className="mb-6" data-id="element-8182">
            <h2 className="text-[20px] font-semibold text-[#0F172A]" data-id="element-8183">
              Fallback Action
            </h2>
            <p className="text-[14px] text-[#64748B] mt-1" data-id="element-8184">
              What happens if all retry attempts fail
            </p>
          </div>

          <div className="p-4 bg-[#E0F2FE] border border-[#7DD3FC] rounded-md flex items-start gap-3" data-id="element-8185">
            <Info className="w-5 h-5 text-[#0369A1] flex-shrink-0 mt-0.5" data-id="element-8186" />
            <p className="text-[14px] text-[#0369A1]" data-id="element-8187">
              After all retry attempts are exhausted, the subscription will be
              placed On Hold. The customer will be notified and can update their
              payment method to resume.
            </p>
          </div>
        </Card>

        {/* Section 5: Customer Notifications */}
        <Card data-id="element-8188">
          <div className="mb-6" data-id="element-8189">
            <h2 className="text-[20px] font-semibold text-[#0F172A]" data-id="element-8190">
              Customer Notifications
            </h2>
            <p className="text-[14px] text-[#64748B] mt-1" data-id="element-8191">
              Configure what customers receive during the recovery process
            </p>
          </div>

          <div className="space-y-4 mb-6" data-id="element-8192">
            <h3 className="text-[14px] font-semibold text-[#475569] uppercase tracking-wider" data-id="element-8193">
              Email Templates
            </h3>

            {[{
            id: 'initial',
            label: 'Initial Failure Email',
            description: 'Sent immediately when a payment fails',
            enabled: initialFailureEmail,
            setEnabled: setInitialFailureEmail
          }, {
            id: 'retry',
            label: 'Retry Reminder Email',
            description: 'Sent before each retry attempt',
            enabled: retryReminderEmail,
            setEnabled: setRetryReminderEmail
          }, {
            id: 'recovered',
            label: 'Payment Recovered Email',
            description: 'Sent when payment is successfully recovered',
            enabled: paymentRecoveredEmail,
            setEnabled: setPaymentRecoveredEmail
          }, {
            id: 'warning',
            label: 'Final Warning Email',
            description: 'Sent before fallback action is executed',
            enabled: finalWarningEmail,
            setEnabled: setFinalWarningEmail
          }, {
            id: 'onhold',
            label: 'Subscription On Hold Email',
            description: 'Sent when subscription is placed on hold due to failed payments',
            enabled: subscriptionOnHoldEmail,
            setEnabled: setSubscriptionOnHoldEmail
          }].map(template => <div key={template.id} className="flex items-center justify-between py-3 border-b border-[#E2E8F0] last:border-0" data-id="element-8194">
                <div className="flex-1" data-id="element-8195">
                  <div className="flex items-center gap-3" data-id="element-8196">
                    <h4 className="text-[14px] font-medium text-[#0F172A]" data-id="element-8197">
                      {template.label}
                    </h4>
                    <button onClick={e => {
                  e.stopPropagation();
                  template.setEnabled(!template.enabled);
                  saveSetting();
                }} className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${template.enabled ? 'bg-[#0EA5E9]' : 'bg-[#CBD5E1]'}`} data-id="element-8198">
                      <span className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${template.enabled ? 'translate-x-5' : 'translate-x-1'}`} data-id="element-8199" />
                    </button>
                  </div>
                  <p className="text-[13px] text-[#64748B] mt-1" data-id="element-8200">
                    {template.description}
                  </p>
                </div>
                <button className="text-[14px] font-medium text-[#0EA5E9] hover:text-[#0284C7] transition-colors flex items-center gap-1" data-id="element-8201">
                  Preview & Edit
                  <ExternalLink className="w-3 h-3" data-id="element-8202" />
                </button>
              </div>)}
          </div>

          <div className="pt-4 border-t border-[#E2E8F0]" data-id="element-8203">
            <div className="flex items-center justify-between mb-3" data-id="element-8204">
              <h3 className="text-[14px] font-semibold text-[#475569] uppercase tracking-wider" data-id="element-8205">
                SMS Notifications
              </h3>
              <button onClick={() => {
              setSmsEnabled(!smsEnabled);
              saveSetting();
            }} className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${smsEnabled ? 'bg-[#0EA5E9]' : 'bg-[#CBD5E1]'}`} data-id="element-8206">
                <span className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${smsEnabled ? 'translate-x-5' : 'translate-x-1'}`} data-id="element-8207" />
              </button>
            </div>

            {smsEnabled && <div className="space-y-3" data-id="element-8208">
                <div className="p-4 bg-[#E0F2FE] border border-[#7DD3FC] rounded-md flex items-start gap-3" data-id="element-8209">
                  <Info className="w-5 h-5 text-[#0369A1] flex-shrink-0 mt-0.5" data-id="element-8210" />
                  <div className="flex-1" data-id="element-8211">
                    <p className="text-[14px] text-[#0369A1] mb-2" data-id="element-8212">
                      SMS requires Klaviyo or Postscript integration. Configure
                      in Notifications settings.
                    </p>
                    <button onClick={() => navigate('/integrations')} className="text-[14px] font-medium text-[#0369A1] hover:text-[#0284C7] transition-colors flex items-center gap-1 underline" data-id="element-8213">
                      Configure SMS Integration
                      <ExternalLink className="w-3 h-3" data-id="element-8214" />
                    </button>
                  </div>
                </div>
              </div>}
          </div>
        </Card>

        {/* Section 6: Update Payment Link */}
        <Card data-id="element-8215">
          <div className="mb-6" data-id="element-8216">
            <h2 className="text-[20px] font-semibold text-[#0F172A]" data-id="element-8217">
              Update Payment Link
            </h2>
            <p className="text-[14px] text-[#64748B] mt-1" data-id="element-8218">
              Allow customers to easily update their payment method
            </p>
          </div>

          <div className="flex items-start justify-between py-4 border-b border-[#E2E8F0]" data-id="element-8219">
            <div className="flex-1 pr-6" data-id="element-8220">
              <h3 className="text-[16px] font-semibold text-[#0F172A] mb-1" data-id="element-8221">
                Include payment update link in emails
              </h3>
              <p className="text-[14px] text-[#64748B]" data-id="element-8222">
                Add a secure link in recovery emails that takes customers
                directly to update their payment method
              </p>
            </div>
            <button onClick={() => {
            setPaymentLinkEnabled(!paymentLinkEnabled);
            saveSetting();
          }} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:ring-offset-2 flex-shrink-0 ${paymentLinkEnabled ? 'bg-[#0EA5E9]' : 'bg-[#CBD5E1]'}`} role="switch" aria-checked={paymentLinkEnabled} data-id="element-8223">
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${paymentLinkEnabled ? 'translate-x-6' : 'translate-x-1'}`} data-id="element-8224" />
            </button>
          </div>

          {paymentLinkEnabled && <div className="pt-4" data-id="element-8225">
              <label className="block text-[12px] font-medium text-[#475569] mb-2" data-id="element-8226">
                Link expiration
              </label>
              <select value={linkExpiration} onChange={e => {
            setLinkExpiration(e.target.value);
            saveSetting();
          }} className="w-full max-w-xs h-[40px] pl-3 pr-10 border border-[#CBD5E1] rounded-md text-[14px] text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:border-transparent appearance-none bg-no-repeat bg-right bg-white" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%23475569' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
            backgroundPosition: 'right 12px center',
            backgroundSize: '16px 16px'
          }} data-id="element-8227">
                <option value="24 hours" data-id="element-8228">24 hours</option>
                <option value="48 hours" data-id="element-8229">48 hours</option>
                <option value="7 days" data-id="element-8230">7 days</option>
                <option value="30 days" data-id="element-8231">30 days</option>
                <option value="Never" data-id="element-8232">Never</option>
              </select>
              <p className="text-[12px] text-[#64748B] mt-1" data-id="element-8233">
                How long the payment update link remains valid
              </p>
            </div>}
        </Card>

        {/* Section 7: Internal Notifications */}
        <Card data-id="element-8234">
          <div className="mb-6" data-id="element-8235">
            <h2 className="text-[20px] font-semibold text-[#0F172A]" data-id="element-8236">
              Internal Notifications
            </h2>
            <p className="text-[14px] text-[#64748B] mt-1" data-id="element-8237">
              Get notified about payment recovery events
            </p>
          </div>

          <div className="space-y-6" data-id="element-8238">
            {[{
            id: 'failure',
            title: 'Notify on payment failure',
            description: 'Receive notification when a payment fails',
            enabled: notifyOnFailure,
            setEnabled: setNotifyOnFailure
          }, {
            id: 'recovery',
            title: 'Notify on successful recovery',
            description: 'Receive notification when a payment is recovered',
            enabled: notifyOnRecovery,
            setEnabled: setNotifyOnRecovery
          }, {
            id: 'fallback',
            title: 'Notify on fallback action',
            description: 'Receive notification when a subscription is placed on hold due to failed payments',
            enabled: notifyOnFallback,
            setEnabled: setNotifyOnFallback
          }, {
            id: 'summary',
            title: 'Daily recovery summary',
            description: 'Receive a daily email summarizing payment recovery status',
            enabled: dailySummary,
            setEnabled: setDailySummary
          }].map(notification => <div key={notification.id} className="flex items-start justify-between py-4 border-b border-[#E2E8F0] last:border-0" data-id="element-8239">
                <div className="flex-1 pr-6" data-id="element-8240">
                  <h3 className="text-[16px] font-semibold text-[#0F172A] mb-1" data-id="element-8241">
                    {notification.title}
                  </h3>
                  <p className="text-[14px] text-[#64748B]" data-id="element-8242">
                    {notification.description}
                  </p>
                </div>
                <button onClick={() => {
              notification.setEnabled(!notification.enabled);
              saveSetting();
            }} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:ring-offset-2 flex-shrink-0 ${notification.enabled ? 'bg-[#0EA5E9]' : 'bg-[#CBD5E1]'}`} role="switch" aria-checked={notification.enabled} data-id="element-8243">
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${notification.enabled ? 'translate-x-6' : 'translate-x-1'}`} data-id="element-8244" />
                </button>
              </div>)}
          </div>

          <div className="pt-6 border-t border-[#E2E8F0] mt-6" data-id="element-8245">
            <label className="block text-[12px] font-medium text-[#475569] mb-2" data-id="element-8246">
              Send notifications to
            </label>
            <div className="flex items-center gap-2 mb-2" data-id="element-8247">
              <input type="email" value={newEmail} onChange={e => setNewEmail(e.target.value)} onKeyPress={e => {
              if (e.key === 'Enter') {
                handleAddEmail();
              }
            }} placeholder="email@example.com" className="flex-1 h-[40px] px-3 border border-[#CBD5E1] rounded-md text-[14px] text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:border-transparent" data-id="element-8248" />
              <button onClick={handleAddEmail} className="h-[40px] px-4 bg-[#0EA5E9] text-white text-[14px] font-medium rounded-md hover:bg-[#0284C7] transition-colors" data-id="element-8249">
                + Add
              </button>
            </div>
            {notificationEmails.length === 0 && <p className="text-[12px] text-[#64748B] mb-3" data-id="element-8250">
                Store owner email will be used by default
              </p>}
            <div className="flex flex-wrap gap-2 mt-3" data-id="element-8251">
              {notificationEmails.map(email => <span key={email} className="inline-flex items-center gap-2 px-3 py-1 bg-[#E0F2FE] text-[#0369A1] rounded-full text-[13px]" data-id="element-8252">
                  {email}
                  <button onClick={() => handleRemoveEmail(email)} className="hover:text-[#EF4444] transition-colors" data-id="element-8253">
                    ×
                  </button>
                </span>)}
            </div>
          </div>
        </Card>
      </div>

      {/* Toast Notification */}
      {showToast && <div className="fixed bottom-6 right-6 z-50 animate-in slide-in-from-bottom-2 duration-200" data-id="element-8254">
          <div className="bg-white border border-[#CBD5E1] rounded-lg shadow-lg px-4 py-3 flex items-center gap-3 min-w-[320px]" data-id="element-8255">
            <div className="w-1 h-10 bg-[#10B981] rounded-full" data-id="element-8256" />
            <div className="flex-1" data-id="element-8257">
              <p className="text-[14px] font-semibold text-[#0F172A]" data-id="element-8258">
                Settings saved
              </p>
              <p className="text-[13px] text-[#64748B]" data-id="element-8259">{toastMessage}</p>
            </div>
          </div>
        </div>}
    </div>;
}